import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useId, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowUpRight, Check, ChevronDown, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO } from '@/components/motion/reveal'
import { countries, industryOptions } from '@/constants/content'

type FieldName = 'firstName' | 'lastName' | 'email' | 'country' | 'company' | 'industry' | 'website' | 'phone' | 'details'

type Values = Record<FieldName, string> & { marketingConsent: boolean, nickname: string }

const EMPTY: Values = {
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    company: '',
    industry: '',
    website: '',
    phone: '',
    details: '',
    marketingConsent: false,
    nickname: ''
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const validate = (values: Values): Partial<Record<FieldName, string>> => {
    const errors: Partial<Record<FieldName, string>> = {}

    if (!values.firstName.trim()) errors.firstName = 'Please enter your first name.'
    if (!values.lastName.trim()) errors.lastName = 'Please enter your last name.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter your email address.'
    if (!values.country) errors.country = 'Please select your country.'
    if (!values.company.trim()) errors.company = 'Please enter your company name.'
    if (!values.industry) errors.industry = 'Please select your industry.'
    if (!values.website.trim()) errors.website = 'Please enter your company website.'

    return errors
}

interface FieldProps {
    id: string
    name: FieldName
    label: string
    value: string
    error?: string
    optional?: boolean
    type?: string
    autoComplete?: string
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
    options?: string[]
    multiline?: boolean
    className?: string
}

const Field: FC<FieldProps> = ({ id, name, label, value, error, optional, type = 'text', autoComplete, onChange, options, multiline, className }): ReactNode => {
    const fieldId = `${id}-${name}`
    const errorId = `${fieldId}-error`
    const base = 'peer w-full border-0 border-b bg-transparent pb-3 pt-7 text-lg text-tone outline-none transition-colors duration-300 placeholder:text-transparent'
    const border = error ? 'border-ember' : 'border-tone-line focus:border-tone'

    return (
        <div className={cn('relative', className)}>
            {options ? (
                <>
                    <select
                        id={fieldId}
                        name={name}
                        value={value}
                        onChange={onChange}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? errorId : undefined}
                        className={cn(base, border, 'appearance-none pr-8 [color-scheme:dark] [&_option]:bg-[#110b20] [&_option]:text-mist', !value && 'text-tone-muted')}
                    >
                        <option value=''>
                            Please select
                        </option>
                        {options.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <ChevronDown className='pointer-events-none absolute bottom-4 right-1 size-4 text-tone-muted' aria-hidden />
                </>
            ) : multiline ? (
                <textarea
                    id={fieldId}
                    name={name}
                    value={value}
                    onChange={onChange}
                    rows={4}
                    placeholder={label}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                    className={cn(base, border, 'resize-none')}
                />
            ) : (
                <input
                    id={fieldId}
                    name={name}
                    type={type}
                    value={value}
                    onChange={onChange}
                    autoComplete={autoComplete}
                    placeholder={label}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                    className={cn(base, border)}
                />
            )}

            <label
                htmlFor={fieldId}
                className={cn(
                    'pointer-events-none absolute left-0 top-0 t-label transition-colors duration-300',
                    error ? 'text-ember' : 'text-tone-muted peer-focus:text-tone'
                )}
            >
                {label}
                {optional && (
                    <span className='ml-2 normal-case tracking-normal opacity-70'>
                        (optional)
                    </span>
                )}
            </label>

            {error && (
                <p id={errorId} className='mt-2 text-sm text-ember'>
                    {error}
                </p>
            )}
        </div>
    )
}

interface ContactFormProps {
    source: string
}

export const ContactForm: FC<ContactFormProps> = ({ source }): ReactNode => {
    const id = useId()
    const [values, setValues] = useState<Values>(EMPTY)
    const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({})
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle')
    const [message, setMessage] = useState('')

    const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const name = event.target.name as FieldName

        setValues(current => ({ ...current, [name]: event.target.value }))
        if (errors[name]) setErrors(current => ({ ...current, [name]: undefined }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const found = validate(values)

        setErrors(found)

        if (Object.keys(found).length > 0) {
            setStatus('failed')
            setMessage('Please check the highlighted fields.')

            const first = Object.keys(found)[0]

            document.getElementById(`${id}-${first}`)?.focus()
            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, source })
            })

            const data = (await response.json().catch(() => null)) as { message?: string, errors?: Partial<Record<FieldName, string>> } | null

            if (!response.ok) {
                if (data?.errors) setErrors(data.errors)

                throw new Error(data?.message ?? 'Oops! Something went wrong while submitting the form.')
            }

            setStatus('sent')
            setValues(EMPTY)
        }
        catch (error) {
            setStatus('failed')
            setMessage(error instanceof Error ? error.message : 'Oops! Something went wrong while submitting the form.')
        }
    }

    return (
        <AnimatePresence mode='wait'>
            {status === 'sent' ? (
                <motion.div
                    key='sent'
                    role='status'
                    className='flex min-h-[32rem] flex-col justify-center gap-8'
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE_OUT_EXPO }}
                >
                    <span className='flex size-20 items-center justify-center rounded-full bg-teal text-void'>
                        <Check className='size-9' aria-hidden />
                    </span>
                    <p className='font-heading text-[clamp(2.5rem,5vw,4.5rem)] font-semibold uppercase leading-[0.9] tracking-[-0.035em] wdth-75'>
                        Thank you! Your submission has been received!
                    </p>
                    <p className='t-lead max-w-md text-tone-muted'>
                        The right person from our team will be in touch.
                    </p>
                    <button
                        type='button'
                        onClick={() => setStatus('idle')}
                        className='link-line self-start text-sm font-semibold'
                    >
                        Send another request
                    </button>
                </motion.div>
            ) : (
                <motion.form
                    key='form'
                    noValidate
                    onSubmit={onSubmit}
                    className='grid gap-x-8 gap-y-9 md:grid-cols-2'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                >
                    <div aria-hidden className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                        <label htmlFor={`${id}-nickname`}>
                            Leave this field empty
                        </label>
                        <input
                            id={`${id}-nickname`}
                            name='nickname'
                            tabIndex={-1}
                            autoComplete='off'
                            value={values.nickname}
                            onChange={event => setValues(current => ({ ...current, nickname: event.target.value }))}
                        />
                    </div>

                    <Field id={id} name='firstName' label='First name' autoComplete='given-name' value={values.firstName} error={errors.firstName} onChange={onChange} />
                    <Field id={id} name='lastName' label='Last name' autoComplete='family-name' value={values.lastName} error={errors.lastName} onChange={onChange} />
                    <Field id={id} name='email' type='email' label='Business email' autoComplete='email' value={values.email} error={errors.email} onChange={onChange} />
                    <Field id={id} name='country' label='Country' value={values.country} error={errors.country} onChange={onChange} options={countries} />
                    <Field id={id} name='company' label='Company name' autoComplete='organization' value={values.company} error={errors.company} onChange={onChange} />
                    <Field id={id} name='industry' label='Industry' value={values.industry} error={errors.industry} onChange={onChange} options={industryOptions} />
                    <Field id={id} name='website' type='url' label='Website' autoComplete='url' value={values.website} error={errors.website} onChange={onChange} />
                    <Field id={id} name='phone' type='tel' label='Phone number' autoComplete='tel' optional value={values.phone} onChange={onChange} />
                    <Field id={id} name='details' label='Additional details' optional multiline value={values.details} onChange={onChange} className='md:col-span-2' />

                    <label htmlFor={`${id}-consent`} className='group flex cursor-pointer items-start gap-4 md:col-span-2'>
                        <input
                            id={`${id}-consent`}
                            type='checkbox'
                            checked={values.marketingConsent}
                            onChange={event => setValues(current => ({ ...current, marketingConsent: event.target.checked }))}
                            className='peer sr-only'
                        />
                        <span className='mt-0.5 flex size-5 shrink-0 items-center justify-center border border-tone-line transition-colors peer-checked:border-tone peer-checked:bg-tone peer-checked:text-tone-bg peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-tone-accent'>
                            <Check className={cn('size-3.5 transition-opacity', values.marketingConsent ? 'opacity-100' : 'opacity-0')} aria-hidden />
                        </span>
                        <span className='text-sm leading-relaxed text-tone-muted'>
                            I agree to receive other communications from nSure.ai.
                        </span>
                    </label>

                    <div className='flex flex-col gap-6 md:col-span-2 md:flex-row md:items-center md:justify-between'>
                        <p className='max-w-sm text-sm leading-relaxed text-tone-muted'>
                            By submitting, you agree to the processing of your details as described in our{' '}
                            <Link href='/privacy-policy' className='text-tone underline underline-offset-4'>
                                privacy policy
                            </Link>
                            .
                        </p>

                        <button
                            type='submit'
                            disabled={status === 'sending'}
                            data-cursor='Send'
                            className='group relative inline-flex h-16 items-center gap-5 overflow-hidden bg-violet pl-7 pr-2 text-base font-semibold text-white transition-colors duration-500 hover:text-void disabled:opacity-70'
                        >
                            <span className='absolute inset-0 origin-left scale-x-0 bg-mist transition-transform duration-700 ease-out-expo group-hover:scale-x-100' aria-hidden />
                            <span className='relative'>
                                {status === 'sending' ? 'Sending' : 'Submit'}
                            </span>
                            <span className='relative flex size-12 items-center justify-center bg-void/15'>
                                {status === 'sending'
                                    ? <LoaderCircle className='size-5 animate-spin' aria-hidden />
                                    : <ArrowUpRight className='size-5 transition-transform duration-500 group-hover:rotate-45' aria-hidden />}
                            </span>
                        </button>
                    </div>

                    <p role='alert' aria-live='assertive' className={cn('min-h-5 text-sm md:col-span-2', status === 'failed' ? 'text-ember' : 'text-tone-muted')}>
                        {message}
                    </p>
                </motion.form>
            )}
        </AnimatePresence>
    )
}
